/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useContext, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  useTable,
  useSortBy,
  usePagination,
  useFilters,
  useExpanded,
} from "react-table";
import { Input } from "components/form";
import { TableWrapper } from "components/common/ReportTable";
import {
  Button,
  Link,
  FlexView,
  Card,
  Icon,
  LoadingOverlay,
} from "components/common";
import { InspectionReportContext } from "stores/InspectionReportStore";
import { UserContext } from "stores/UserStore";
import styled from "styled-components";
import moment from "moment";
import _ from "lodash";
import { useHistory } from "react-router-dom";
import WorkflowModal from "containers/inspection-report/WorkflowModal";
import ReactTooltip from "react-tooltip";

const Table = styled(TableWrapper)`
  display: flex;
  flex-direction: column;

  .table-wrap {
    flex: 1;
    overflow: auto;
  }

  th input {
    font-size: ${({ theme }) => theme.fontSizes.small};
    padding: 4px;
    min-height: 0px;
  }
`;

const DefaultColumnFilter = ({
  column: { filterValue, preFilteredRows, setFilter },
}) => {
  const onChange = useCallback(
    (e) => {
      setFilter(e.target.value || undefined); // Set undefined to remove the filter entirely
    },
    [setFilter]
  );

  return (
    <Input
      value={filterValue || ""}
      onChange={onChange}
      placeholder="Type to Filter"
      margin="0px"
      width="100%"
    />
  );
};

const sortByDate = (rowA, rowB, columnId, desc) => {
  const valueA = _.get(rowA, `original.${columnId}`);
  const valueB = _.get(rowB, `original.${columnId}`);
  return moment.utc(valueA).isAfter(valueB) ? 1 : -1;
};

const tableOptions = {
  defaultColumn: {
    Filter: DefaultColumnFilter,
  },
  getSubRows: (row) => row.children || [],
  paginateExpandedRows: false,
  autoResetExpanded: false,
  autoResetPage: false,
  sortTypes: {
    date: sortByDate,
  },
};

const InspectionReportTable = () => {
  const DEFAULT_URL = "inspection-report";
  const history = useHistory();
  const { t } = useTranslation();
  const {
    // filteredinspectionReportData: { tableData },
    tableData,
    setReportToApproval,
    setApprovalType,
    getInspectionReportWorkflowData,
    getInspectionReportDocument,
    filters,
    showFinalized,
    getInspectionReportData,
    totalData,
    totalPages,
    page: actualPage
  } = useContext(InspectionReportContext);
  const { currentUser, registryLog } = useContext(UserContext);
  const [loading, setLoading] = useState(false);

  const [showWorkflowModal, setShowWorkflowModal] = useState(false);
  const toggleWorkflowModal = () =>
    setShowWorkflowModal((currentWorkflowState) => !currentWorkflowState);

  const data = useMemo(() => _.chain(tableData).value(), [tableData]);

  const onApproval = useCallback(
    (report, aprrovalType) => () => {
      setReportToApproval(report);
      setApprovalType(aprrovalType);
      //registryLogOld("Go to Approval", DEFAULT_URL);
      registryLog(DEFAULT_URL, "navigation", "approval");
      history.push("/inspection-report/approval");
    },
    [history]
  );

  const onWorkflow = useCallback(
    (report) => () => {
      setLoading(true);
      getInspectionReportWorkflowData(report)
        .then(() => {
          setShowWorkflowModal(true);
        })
        .catch((e) => {
          console.debug(e);
        })
        .finally(() => setLoading(false));
    },
    [history]
  );

  const onDownload = useCallback(
    (report) => () => {
      //registryLogOld("Download Report", DEFAULT_URL);
      registryLog(DEFAULT_URL, "download", "report");
      setLoading(true);
      getInspectionReportDocument(report)
        .then((response) => {
          if (response && response.document) {
            window.open(response.document, "_blank");
          }
        })
        .catch((e) => {
          console.debug(e);
        })
        .finally(() => setLoading(false));
    },
    [history]
  );

  const columns = useMemo(() => {
    const result = [];
    result.push({
      Header: t("inspectionReport.sot"),
      accessor: "sot",
      Cell: ({ cell: { row } }) => (
        <FlexView
          flexDirection="row"
          alignItems="center"
          justifyContent="flex-start"
        >
          {row.values.workflow_status !== "started" &&
            row.values.workflow_status !== "sgro-loaded" ? (
            <Icon
              name="download"
              color="primary"
              margin="2px 5px"
              width="16px"
              height="16px"
              title={t("inspectionReport.download-avaiable")}
              onClick={onDownload(row.values)}
            ></Icon>
          ) : (
            <Icon
              name="download"
              color="error"
              margin="2px 5px"
              width="16px"
              height="16px"
              title={t("inspectionReport.download-unavaiable")}
            ></Icon>
          )}
          <div style={{ textAlign: "left", fontWeight: 400, fontSize: "13px" }}>
            {row.values.sot}
          </div>
        </FlexView>
      ),
      customHeaderProps: {
        style: {
          minWidth: "80px",
        },
      },

      disableFilters: true,
    });
    result.push({
      Header: t("inspectionReport.item"),
      accessor: "sot_item",
      customCellProps: {
        style: {
          textAlign: "center",
        },
      },
      disableFilters: true,
    });
    result.push({
      Header: t("inspectionReport.id"),
      accessor: "report_id",
      customCellProps: {
        style: {
          textAlign: "center",
        },
      },
      disableFilters: true,
    });
    result.push({
      Header: t("inspectionReport.version"),
      accessor: "report_version",
      customCellProps: {
        style: {
          textAlign: "center",
        },
      },
      disableFilters: true,
    });
    result.push({
      Header: t("inspectionReport.inspectionDate"),
      accessor: "inspection_date",
      Cell: ({ cell: { value } }) =>
        value ? moment.utc(value).format("DD/MM/YYYY") : "",
      customCellProps: {
        style: {
          textAlign: "center",
        },
      },
      sortType: "date",
      disableFilters: true,
    });
    result.push({
      Header: t("inspectionReport.creationDate"),
      accessor: "report_date",
      Cell: ({ cell: { value } }) =>
        value ? moment.utc(value).format("DD/MM/YYYY") : "",
      customCellProps: {
        style: {
          textAlign: "center",
        },
      },
      sortType: "date",
      disableFilters: true,
    });
    result.push({
      Header: t("inspectionReport.processStatus"),
      accessor: "process_status",
      Cell: ({ cell: { row } }) => (
        <FlexView
          flexDirection="row"
          alignItems="center"
          justifyContent="flex-start"
        >
          {row.values.process_status === "approved" ||
            row.values.process_status === "internal-approved" ? (
            <Icon
              name="check"
              color="success"
              margin="2px 5px"
              width="18px"
              height="18px"
              title={t("inspectionReport.success")}
            ></Icon>
          ) : row.values.process_status === "customer-rejected" ? (
            <Icon
              name="warning"
              color="error"
              margin="2px 5px"
              width="18px"
              height="18px"
              title={row.values.customer_user_comment}
            ></Icon>
          ) : row.values.process_status === "tpi-rejected" ? (
            <Icon
              name="warning"
              color="error"
              margin="2px 5px"
              width="18px"
              height="18px"
              title={row.values.tpi_user_comment}
            ></Icon>
          ) : (
            <Icon
              name="clock"
              color="secondary"
              margin="2px 5px"
              width="18px"
              height="18px"
              title={t("inspectionReport.waiting")}
            ></Icon>
          )}
          <div style={{ textAlign: "left", fontWeight: 700, fontSize: "13px" }}>
            {t("inspectionReport." + row.values.process_status)}
          </div>
        </FlexView>
      ),
      customHeaderProps: {
        style: {
          minWidth: "90px",
        },
      },
      customCellProps: {
        style: {
          margin: "2px",
          padding: "2px",
        },
      },
      disableFilters: true,
    });
    result.push({
      Header: t("Info."),
      Cell: ({ cell: { row } }) => (
        <FlexView
          flexDirection="row"
          alignItems="center"
          justifyContent="center"
          data-tip
          data-for={"proc_status_" + row.values.report_id}
        >
          {row.values.process_status === "customer-rejected" ? (
            <>
              <Icon
                name="info"
                color="info"
                margin="2px 5px"
                width="18px"
                height="18px"
              ></Icon>
              <ReactTooltip
                id={"proc_status_" + row.values.report_id}
                place="top"
                type="warning"
                backgroundColor="#59CBE4"
                effect="float"
              >
                {row.values.customer_user_comment}
              </ReactTooltip>
            </>
          ) : row.values.process_status === "tpi-rejected" ? (
            <>
              <Icon
                name="info"
                color="info"
                margin="2px 5px"
                width="18px"
                height="18px"
              ></Icon>
              <ReactTooltip
                id={"proc_status_" + row.values.report_id}
                place="top"
                type="warning"
                backgroundColor="#59CBE4"
                effect="float"
              >
                {row.values.tpi_user_comment}
              </ReactTooltip>
            </>
          ) : row.values.process_status === "internal-approved" ? (
            <>
              <Icon
                name="info"
                color="info"
                margin="2px 5px"
                width="18px"
                height="18px"
              ></Icon>
              <ReactTooltip
                id={"proc_status_" + row.values.report_id}
                place="top"
                type="warning"
                backgroundColor="#59CBE4"
                effect="float"
              >
                {t("inspectionReportApproval.appoved-by")}&nbsp;
                {row.values.customer_user_name}
                <br />
                {t("inspectionReportApproval.comments")}:&nbsp;
                {row.values.customer_user_comment}
              </ReactTooltip>
            </>
          ) : (
            <></>
          )}
        </FlexView>
      ),
      customHeaderProps: {
        style: {
          margin: "2px",
          padding: "2px",
          width: "10px",
          minWidth: "10px",
        },
      },
      customCellProps: {
        style: {
          margin: "2px",
          padding: "2px",
        },
      },
      disableFilters: true,
      disableSortBy: true,
    });
    if (
      currentUser &&
      currentUser.roles.INSPECTION_REPORT &&
      currentUser.roles.INSPECTION_REPORT.WORKFLOW_MANAGER
    ) {
      result.push({
        Header: t("inspectionReport.current-step"),
        accessor: "workflow_status",
        Cell: ({ cell: { row } }) => (
          <FlexView
            flexDirection="row"
            alignItems="center"
            justifyContent="flex-start"
          >
            {row.values.workflow_status === "tpi-rejected" ? (
              <Icon
                name="warning"
                color="error"
                margin="2px 5px"
                width="18px"
                height="18px"
                title={row.values.tpi_user_comment}
              ></Icon>
            ) : row.values.workflow_status === "customer-rejected" ? (
              <Icon
                name="warning"
                color="error"
                margin="2px 5px"
                width="18px"
                height="18px"
                title={row.values.customer_user_comment}
              ></Icon>
            ) : (
              <Icon
                name="check"
                color="success"
                margin="2px 5px"
                width="18px"
                height="18px"
                title={t("inspectionReport.success")}
              ></Icon>
            )}
            <div
              style={{ textAlign: "left", fontWeight: 700, fontSize: "13px" }}
            >
              {t("inspectionReport." + row.values.workflow_status)}
            </div>
          </FlexView>
        ),
        customHeaderProps: {
          style: {
            minWidth: "130px",
          },
        },
        customCellProps: {
          style: {
            margin: "2px",
            padding: "2px",
          },
        },
        disableFilters: true,
      });
      result.push({
        Header: t("inspectionReport.next-step"),
        accessor: "next_workflow_status",
        Cell: ({ cell: { row } }) => (
          <FlexView
            flexDirection="row"
            alignItems="center"
            justifyContent="flex-start"
          >
            {row.values.workflow_fail_message ? (
              <Icon
                name="warning"
                color="error"
                margin="2px 5px"
                width="18px"
                height="18px"
                title={t(
                  "inspectionReport." + row.values.workflow_fail_message
                )}
              ></Icon>
            ) : row.values.workflow_status === "finished" ? (
              <Icon
                name="check"
                color="success"
                margin="2px 5px"
                width="18px"
                height="18px"
                title={t("inspectionReport.success")}
              ></Icon>
            ) : (
              <Icon
                name="clock"
                color="secondary"
                margin="2px 5px"
                width="18px"
                height="18px"
                title={t("inspectionReport.waiting")}
              ></Icon>
            )}
            <div
              style={{ textAlign: "left", fontWeight: 700, fontSize: "13px" }}
            >
              {t("inspectionReport." + row.values.next_workflow_status)}
            </div>
          </FlexView>
        ),
        customHeaderProps: {
          style: {
            minWidth: "130px",
          },
        },
        customCellProps: {
          style: {
            margin: "0px",
            padding: "0px",
          },
        },
        disableFilters: true,
      });
    } else {
      result.push({
        accessor: "workflow_status",
        customHeaderProps: { style: { display: "none" } },
        customCellProps: { style: { display: "none" } },
      });
      result.push({
        accessor: "next_workflow_status",
        customHeaderProps: { style: { display: "none" } },
        customCellProps: { style: { display: "none" } },
      });
    }
    if (
      currentUser &&
      currentUser.roles.INSPECTION_REPORT &&
      (currentUser.roles.INSPECTION_REPORT.TPI_APPROVAL ||
        currentUser.roles.INSPECTION_REPORT.CUSTOMER_APPROVAL ||
        currentUser.roles.INSPECTION_REPORT.INTERNAL_APPROVAL ||
        currentUser.roles.INSPECTION_REPORT.WORKFLOW_MANAGER)
    ) {
      result.push({
        Header: t("inspectionReport.actions"),
        accessor: "workflow_fail_message",
        Cell: ({ cell: { row } }) =>
          currentUser && currentUser.roles.INSPECTION_REPORT ? (
            <FlexView
              flexDirection="row"
              alignItems="center"
              justifyContent="center"
              width="100%"
              minWidth="50px"
              margin="0"
              padding="0"
            >
              {currentUser.roles.INSPECTION_REPORT.TPI_APPROVAL ? (
                <Button
                  key={row.values.report_id + "_1"}
                  disabled={
                    row.values.next_workflow_status !== "to-tpi-approval"
                  }
                  margin="0px 6px"
                  padding="3px 16px"
                  borderRadius="50px"
                  backgroundColor="success"
                  color="white"
                  fontSize="small"
                  onClick={onApproval(row.values, "to-tpi-approval")}
                >
                  {t("inspectionReport.inspector")}
                </Button>
              ) : null}
              {currentUser.roles.INSPECTION_REPORT.CUSTOMER_APPROVAL ? (
                <Button
                  key={row.values.report_id + "_2"}
                  disabled={
                    row.values.next_workflow_status !== "to-customer-approval"
                  }
                  margin="0px 6px"
                  padding="3px 16px"
                  borderRadius="50px"
                  backgroundColor="success"
                  color="white"
                  fontSize="small"
                  onClick={onApproval(row.values, "to-customer-approval")}
                >
                  {t("inspectionReport.customer")}
                </Button>
              ) : null}
              {currentUser.roles.INSPECTION_REPORT.INTERNAL_APPROVAL ? (
                <Button
                  key={row.values.report_id + "_4"}
                  disabled={
                    row.values.next_workflow_status !== "to-customer-approval"
                  }
                  margin="0px 6px"
                  padding="3px 16px"
                  borderRadius="50px"
                  backgroundColor="success"
                  color="white"
                  fontSize="small"
                  onClick={onApproval(row.values, "to-internal-approval")}
                >
                  {t("inspectionReport.internal-approve")}
                </Button>
              ) : null}
              {currentUser.roles.INSPECTION_REPORT.WORKFLOW_MANAGER ? (
                <Button
                  key={row.values.report_id + "_3"}
                  disabled={!row.values.workflow_fail_message}
                  margin="0px 0px"
                  padding="3px 16px"
                  borderRadius="50px"
                  backgroundColor="secondary"
                  color="white"
                  fontSize="small"
                  onClick={onWorkflow(row.values)}
                >
                  {t("inspectionReport.workflow")}
                </Button>
              ) : null}
            </FlexView>
          ) : null,
        customCellProps: {
          style: {
            margin: "0px",
            padding: "0px",
          },
        },
        disableFilters: true,
        disableSortBy: true,
      });
    }
    result.push({
      accessor: "tpi_user_name",
      customHeaderProps: { style: { display: "none" } },
      customCellProps: { style: { display: "none" } },
    });
    result.push({
      accessor: "customer_user_name",
      customHeaderProps: { style: { display: "none" } },
      customCellProps: { style: { display: "none" } },
    });
    result.push({
      accessor: "tpi_approval_status",
      customHeaderProps: { style: { display: "none" } },
      customCellProps: { style: { display: "none" } },
    });
    result.push({
      accessor: "customer_approval_status",
      customHeaderProps: { style: { display: "none" } },
      customCellProps: { style: { display: "none" } },
    });
    result.push({
      accessor: "tpi_user_comment",
      customHeaderProps: { style: { display: "none" } },
      customCellProps: { style: { display: "none" } },
    });
    result.push({
      accessor: "customer_user_comment",
      customHeaderProps: { style: { display: "none" } },
      customCellProps: { style: { display: "none" } },
    });

    return result;
  }, [t]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    state: { pageIndex },
  } = useTable(
    {
      columns,
      data,
      ...tableOptions,
    },
    useFilters,
    useSortBy,
    useExpanded,
    usePagination
  );

  const getPage = async (page) => {
    setLoading(true)
    await getInspectionReportData(filters, showFinalized, page)
    setLoading(false)
  }

  // const navigationTable = useCallback(
  //   (action, lastPage) => () => {
  //     //registryLogOld(`Go to ${action} page`, DEFAULT_URL);
  //     registryLog(DEFAULT_URL, "pagining", action);
  //     if (action === "first") {
  //       getPage(1);
  //     } else if (action === "previous") {
  //       getPage(1);
  //       // previousPage();
  //     } else if (action === "next") {
  //       nextPage();
  //     } else if (action === "last") {
  //       gotoPage(lastPage);
  //     }
  //   },
  //   []
  // );

  return (
    <Card padding="0px" margin="0px 16px 16px" alignSelf="stretch">
      <Table elevation="none" width="100%" fontSize="13px">
        <div className="table-wrap">
          <table {...getTableProps()}>
            <thead>
              {headerGroups.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => (
                    <th
                      {...column.getHeaderProps(column.customHeaderProps)}
                      className="sortable"
                    >
                      <div {...column.getSortByToggleProps({ title: "" })}>
                        {column.render("Header")}
                        <span>
                          {column.isSorted ? (
                            column.isSortedDesc ? (
                              <svg
                                width="13"
                                height="7"
                                viewBox="0 0 13 7"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                transform="rotate(180)"
                              >
                                <path
                                  d="M0 6.5L6.5 6.5L13 6.5L6.5 0L0 6.5Z"
                                  fill="#777777"
                                />
                              </svg>
                            ) : (
                              <svg
                                width="13"
                                height="7"
                                viewBox="0 0 13 7"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M0 6.5L6.5 6.5L13 6.5L6.5 0L0 6.5Z"
                                  fill="#777777"
                                />
                              </svg>
                            )
                          ) : (
                            ""
                          )}
                        </span>
                      </div>
                      <div>
                        {column.canFilter ? column.render("Filter") : null}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map((row) => {
                prepareRow(row);
                return (
                  <tr
                    {...row.getRowProps({
                      style: {
                        backgroundColor: !row.canExpand ? "#f5f5f5" : "inherit",
                      },
                    })}
                  >
                    {row.cells.map((cell) => {
                      return (
                        <td {...cell.getCellProps(cell.column.customCellProps)}>
                          {cell.render("Cell")}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="pagination">
          <FlexView
            flexDirection="row"
            justifyContent="flex-start"
            alignItems="center"
            flex="1"
          >
            <Input
              inline
              type="number"
              label={t("Go to page")}
              fontSize="13px"
              margin="0px"
              padding="8px 4px"
              defaultValue={pageIndex + 1}
              onChange={(e) => {
                const page = e.target.value ? Number(e.target.value) - 1 : 0;
                //registryLogOld("Go to Page " + (page + 1), DEFAULT_URL);
                registryLog(DEFAULT_URL, "pagining", page + 1);
                getPage(page + 1);
              }}
              width="180px"
            />
          </FlexView>
          <FlexView
            flexDirection="row"
            alignItems="center"
            justifyContent="center"
            fontSize="13px"
          >
            <Link
              noDecoration
              color="gray"
              fontSize="big"
              fontWeight="bold"
              padding="4px"
              margin="0px 4px"
              onClick={() => getPage(0)}
              disabled={actualPage === 0 || totalPages < actualPage + 1}
            >
              {"«"}
            </Link>{" "}
            <Link
              noDecoration
              color="gray"
              fontSize="big"
              fontWeight="bold"
              padding="4px"
              margin="0px 4px"
              onClick={() => getPage(actualPage - 1)}
              disabled={actualPage === 0 || totalPages < actualPage + 1}
            >
              {"‹"}
            </Link>{" "}
            <FlexView flexDirection="row" margin="0px 8px" fontWeight="bold">
              {actualPage + 1} {t("of")} {totalPages}
            </FlexView>
            <Link
              noDecoration
              color="gray"
              fontSize="big"
              fontWeight="bold"
              padding="4px"
              margin="0px 4px"
              onClick={() => getPage(actualPage + 1)}
              disabled={totalPages === actualPage + 1}
            >
              {"›"}
            </Link>{" "}
            <Link
              noDecoration
              color="gray"
              fontSize="big"
              fontWeight="bold"
              padding="4px"
              margin="0px 4px"
              onClick={() => getPage(totalPages - 1)}
              disabled={totalPages === actualPage + 1}
            >
              {"»"}
            </Link>{" "}
          </FlexView>
          <FlexView
            flex="1"
            flexDirection="row"
            justifyContent="flex-end"
            alignItems="center"
          >
            <FlexView
              flexDirection="row"
              alignItems="center"
              fontSize="small"
              borderRadius="component"
              backgroundColor="whitesmoke"
              padding="8px"
              margin="0px 16px"
            >
              <FlexView margin="0px 4px 0px" color="secondary">
                {totalData}
              </FlexView>
              <span>{t("inspectionReport.report")}</span>
            </FlexView>
            {/* <Select
              margin="0px"
              value={pageSize}
              options={[5, 10, 20, 30, 40, 50].map((pageSize) => ({
                value: pageSize,
                label: `${t("Show")} ${pageSize}`,
              }))}
              onChange={(value) => {
                //registryLogOld("Show " + Number(value), DEFAULT_URL);
                registryLog(DEFAULT_URL, "show_pagining", value);
                setPageSize(Number(value));
              }}
            /> */}
          </FlexView>
        </div>
      </Table>
      <WorkflowModal
        isOpen={showWorkflowModal}
        onOutsideClick={toggleWorkflowModal}
      />
      <LoadingOverlay visible={loading} />
    </Card>
  );
};

export default InspectionReportTable;
